import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { TShirt, X, SunHorizon } from "@phosphor-icons/react";
import { MOOD_CLOTHING } from "./WhatToWear";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getMoodClothing(mood) {
  if (!mood) return null;
  const key = mood.toLowerCase().replace(/\s+/g, "_");
  if (MOOD_CLOTHING[key]) return { mood: key, colors: MOOD_CLOTHING[key] };
  if (key.includes("stress") || key.includes("overwhelm")) return { mood: "stressed", colors: MOOD_CLOTHING.stressed };
  if (key.includes("anxi") || key.includes("worry")) return { mood: "anxious", colors: MOOD_CLOTHING.anxious };
  if (key.includes("burn") || key.includes("exhaust")) return { mood: "burned_out", colors: MOOD_CLOTHING.burned_out };
  if (key.includes("confiden") || key.includes("doubt")) return { mood: "low_confidence", colors: MOOD_CLOTHING.low_confidence };
  if (key.includes("sad") || key.includes("down")) return { mood: "sad", colors: MOOD_CLOTHING.sad };
  return { mood: key, colors: MOOD_CLOTHING.calm };
}

function getTodayKey() {
  return `ootd-${new Date().toISOString().slice(0, 10)}`;
}

export default function MoodOutfitOfTheDay() {
  const [outfit, setOutfit] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    // Only show once per day
    const todayKey = getTodayKey();
    if (localStorage.getItem(todayKey) === "dismissed") {
      setDismissed(true);
      setLoaded(true);
      return;
    }

    // Fetch recent mood history to determine dominant mood
    axios.get(`${API}/mood/history?limit=10`, { withCredentials: true })
      .then((res) => {
        const entries = res.data;
        if (!entries || entries.length === 0) {
          setLoaded(true);
          return;
        }

        // Count mood frequencies from recent entries
        const counts = {};
        entries.forEach((e) => {
          const m = e.mood || "calm";
          counts[m] = (counts[m] || 0) + 1;
        });

        // Get the most frequent mood
        const topMood = Object.entries(counts).sort((a, b) => b[1] - a[1])[0][0];
        const data = getMoodClothing(topMood);
        if (data) {
          // Pick one featured color (rotate daily based on date)
          const dayIndex = new Date().getDate() % data.colors.length;
          setOutfit({
            mood: topMood,
            featured: data.colors[dayIndex],
            alternates: data.colors.filter((_, i) => i !== dayIndex),
          });
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem(getTodayKey(), "dismissed");
  };

  if (!loaded || dismissed || !outfit) return null;

  const greeting = new Date().getHours() < 12 ? "Good morning" : new Date().getHours() < 17 ? "Good afternoon" : "Good evening";

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12, height: 0 }}
        transition={{ duration: 0.4 }}
        className="mb-6"
        data-testid="mood-outfit-of-the-day"
      >
        <div
          className="rounded-2xl overflow-hidden border border-black/5"
          style={{ backgroundColor: outfit.featured.hex + "0A" }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <div className="flex items-center gap-2">
              <SunHorizon size={16} weight="duotone" className="text-[#F4A261]" />
              <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-[#F4A261]">
                Outfit of the Day
              </span>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 text-[#78857F] hover:text-[#1A2421] transition-colors"
              data-testid="dismiss-ootd"
            >
              <X size={14} />
            </button>
          </div>

          {/* Featured Color */}
          <div className="px-4 pb-3">
            <p className="text-xs text-[#78857F] mb-3">
              {greeting} — based on your recent mood patterns, here's what to wear today:
            </p>

            <div className="flex items-start gap-3">
              <div
                className="w-16 h-16 rounded-2xl border border-black/5 shrink-0 shadow-sm"
                style={{ backgroundColor: outfit.featured.hex }}
                data-testid="ootd-featured-swatch"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-semibold text-[#1A2421]">{outfit.featured.name}</span>
                  <span className="text-[10px] text-[#78857F] font-mono">{outfit.featured.hex}</span>
                </div>
                <p className="text-xs text-[#78857F] leading-relaxed mt-0.5">
                  {outfit.featured.reason}
                </p>
              </div>
            </div>

            {/* Alternate swatches */}
            {outfit.alternates.length > 0 && (
              <div className="flex items-center gap-2 mt-3 pt-3 border-t border-black/5">
                <TShirt size={14} weight="duotone" className="text-[#78857F] shrink-0" />
                <span className="text-[10px] text-[#78857F]">Also great today:</span>
                {outfit.alternates.map((alt) => (
                  <div
                    key={alt.hex}
                    className="flex items-center gap-1.5"
                    data-testid={`ootd-alt-${alt.hex}`}
                  >
                    <div
                      className="w-5 h-5 rounded-md border border-black/5"
                      style={{ backgroundColor: alt.hex }}
                    />
                    <span className="text-[10px] text-[#78857F]">{alt.name}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
}
